// src/api/products.js
import axios from 'axios';
import qs from 'qs';

// Point this at your own proxy or absolute URL if needed
// const API = axios.create({ baseURL: 'http://localhost:5000/api' });
const API = axios.create({
  //baseURL: 'http://localhost:5000/api',
  baseURL: 'https://kiosk-api.sortswift.com/api',
  // ensures e.g. type[]=Snow&type[]=Artifact
  paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets' })
});

/**
 * Search products (paginated).
 * Supports an optional `inStock` flag:
 *   - inStock = true (default): only returns products the store currently has in stock
 *   - inStock = false: returns products the store does NOT have in stock
 *
 * Registry-driven advanced filters (TKT-2026-102569) are forwarded verbatim
 * (the object is spread into the query string by qs, arrays as `key[]=…`):
 *   - RANGE filters use `<key>Min` / `<key>Max` for: cmc, hp, retreatCost, atk,
 *     def, powerToughness, inkCost, lore, strength, willpower, opCost, opPower,
 *     counter, fabCost, fabDefense.
 *   - FOIL: `foil=true` (no-op for YGO / cat 2).
 *   - CARD-TEXT: `searchCardText=true` (adds rules-text source to `q`).
 *   - ENUM/ARRAY/TYPEAHEAD use the registry `key` itself (arrays as `key[]`):
 *     cardType, creatureType, keywords, artist, legality, pokemonType, stage,
 *     illustrator, regulationMark, weakness, ygoCardType, attribute, monsterType,
 *     inkType, lorcanaCardType, inkable, property, opColor, opCardType,
 *     opAttribute, opSubtypes, fabCardType, fabClass, pitch.
 * Legacy aliases (type/creatureType/legalStatuses/printing/color/rarity) are
 * still accepted by the backend; registry keys are preferred.
 *
 * @param {string} storeName
 * @param {{
 *   q?: string,
 *   page?: number,
 *   size?: number,
 *   categoryId?: number,
 *   groupId?: number,
 *   color?: string[] | string,
 *   colorIdentity?: string[] | string,
 *   rarity?: string[] | string,
 *   cardType?: string[] | string,
 *   creatureType?: string[] | string,
 *   legality?: string[] | string,
 *   frame_effects?: string[] | string,
 *   printing?: string,
 *   foil?: boolean,
 *   searchCardText?: boolean,
 *   single?: boolean,
 *   sealed?: boolean,
 *   custom?: boolean,
 *   sortBy?: string,
 *   inStock?: boolean,
 *   [key: string]: any
 * }} params Any additional registry keys (and their `Min`/`Max` range variants)
 *           are forwarded as-is.
 * @param {{ signal?: AbortSignal, [key: string]: any }} [config] Extra axios
 *        request config, e.g. `{ signal }` to cancel a superseded search. Note
 *        that axios rejects a cancelled request with its own `CanceledError`
 *        (`code: 'ERR_CANCELED'`), NOT a DOM `AbortError`, so callers must test
 *        with `isCanceledError` below rather than `err.name === 'AbortError'`.
 */
export const searchProducts = (storeName, params = {}, config = {}) => {
  // ensure inStock defaults to true if not provided
  const { inStock = true, ...rest } = params;
  // `config` is spread FIRST so a caller can pass `signal` without ever being
  // able to clobber the qs-serialized query params.
  return API.get(`/products/${storeName}/search`, {
    ...config,
    params: { ...rest, inStock }
  });
};

/**
 * Fetch distinct filter values (color, rarity, etc).
 * Always accepts an object of query parameters so that
 * `categoryId`, `groupId`, and any selected filters
 * (including MTG `type`/`creatureType` or non-MTG `subtype`)
 * get serialized into the URL.
 *
 * Response (TKT-2026-102569) includes the legacy keys (colors, rarities, types,
 * creatureTypes, legalStatuses, printings, …) PLUS:
 *   - `facets`: `{ [registryKey]: string[] }` chips/typeahead options
 *   - `ranges`: `{ [registryKey]: { min, max } }` numeric bounds
 *   - `registry`: the registry entries applicable to the requested categoryId
 *
 * @param {string} storeName
 * @param {{
 *   categoryId?: number,
 *   groupId?: number,
 *   color?: string[] | string,
 *   rarity?: string[] | string,
 *   frame_effects?: string[] | string,
 *   type?: string[] | string,
 *   creatureType?: string[] | string,
 *   subtype?: string[] | string,
 *   legalStatuses?: string[] | string,
 *   [key: string]: any
 * }} params
 */
export const fetchFilters = (storeName, params) =>
  API.get(`/products/${storeName}/filters`, { params });

/**
 * Fetch the full filter registry (TKT-2026-102569). Store-independent; returns
 * `{ registry: [{ key, gameIds, control, group, label, defaultEnabled, rangeBounds? }] }`
 * so the kiosk can render game-aware controls. Owner `filterSettings` only
 * enable/disable entries; a missing key falls back to the entry's `defaultEnabled`.
 */
export const fetchFilterRegistry = () =>
  API.get('/products/filters/registry');

/**
 * Fetch up to 5 autocompletion suggestions for product names.
 * @param {string} storeName
 * @param {{ q: string, categoryId?: number, groupId?: number, limit?: number }} params
 * @param {{ signal?: AbortSignal, [key: string]: any }} [config] Extra axios
 *        request config, e.g. `{ signal }` to cancel a superseded lookup.
 */
export const suggestProducts = (storeName, params, config = {}) =>
  API.get(`/products/${storeName}/suggest`, { ...config, params });

/**
 * True when a rejection is a cancelled request rather than a real failure.
 *
 * Lives here so callers do not each take a direct axios dependency just to
 * classify an error. `err.name === 'AbortError'` does NOT work: axios rejects
 * an aborted request with its own `CanceledError` (name `'CanceledError'`,
 * code `'ERR_CANCELED'`), so testing for `AbortError` treats every superseded
 * request as a failed one. TKT-2026-103916
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export const isCanceledError = (err) =>
  axios.isCancel(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError';

/**
 * Fetch all categories.
 * @param {string} storeName
 * @param {{ buylistMode?: boolean, buylistCategoryIds?: number[] }} [params]
 */
export const fetchCategories = (storeName, params) =>
  API.get(`/products/${storeName}/categories`, { params });

/**
 * Fetch all groups/sets for a given categoryId.
 * @param {string} storeName
 * @param {{ categoryId: number, buylistMode?: boolean }} params
 */
export const fetchGroups = (storeName, params) =>
  API.get(`/products/${storeName}/groups`, { params });

/**
 * Verify kiosk PIN for this store.
 */
export const verifyKioskPin = (storeName, pin) =>
  API.post(`/products/${storeName}/kiosk-config/verify-pin`, { pin });

/**
 * Check the store passcode in order to release the fullscreen kiosk lock.
 *
 * Deliberately separate from `verifyKioskPin`. That one is the way IN to the
 * kiosk: it carries a geo fix and a device fingerprint, and it refuses to submit
 * at all when the IP lookup fails. Releasing the lock must not depend on an
 * external geolocation service being reachable, or a store with a flaky
 * connection cannot get to its own machine. This endpoint compares the passcode
 * and nothing else. TKT-2026-105376.
 *
 * @param {string} storeName
 * @param {string} pin
 */
export const verifyKioskUnlock = (storeName, pin) =>
  API.post(`/products/${storeName}/kiosk-config/verify-unlock`, { pin });

/**
 * Fetch the store's kiosk display config (theme/logo/tax/currency) without the
 * PIN-verify side effects. Used to refresh the theme on every kiosk load so admin
 * changes apply even on an already-unlocked kiosk.
 * Returns: { storeLogoUrl, kioskTheme, salesTaxRate, taxOnNetTotal, taxInclusive,
 *            currency, filterSettings }
 * `filterSettings` (TKT-2026-102569) shape:
 *   { version, games:{ [catId]:{ [key]:bool } }, universal:{ [key]:bool }, searchCardText:bool }
 * (verify-pin returns the same `filterSettings`.)
 * @param {string} storeName
 */
export const fetchKioskTheme = (storeName) =>
  API.get(`/products/${storeName}/kiosk-config/theme`);

/**
 * Live in-store variants for the products in the cart (TKT-2026-105225).
 *
 * The cart optimiser re-picks which physical copy each line buys, and that copy
 * is the one checkout reserves, so it has to decide against stock as it is now
 * rather than against whatever was captured when the card was added. A kiosk
 * cart survives reloads, so that could have been hours ago.
 *
 * Returns `{ variants: { [productId]: Variant[] } }`. A product present with an
 * empty array means the store no longer has a buyable copy; a product missing
 * from the map was not refreshed and its line must be left alone.
 * Responds 403 when the store has not enabled cart optimisation.
 *
 * @param {string} storeName
 * @param {Array<number|string>} productIds  max 300
 */
export const fetchCartVariants = (storeName, productIds) =>
  API.post(`/products/${storeName}/cart-variants`, { productIds });

// ─── BUYLIST API ────────────────────────────────────────────────────────────────

/**
 * Get buylist configuration for a store
 * Returns: { hasBuylist: boolean, storeId: string, config: object }
 * @param {string} storeName
 */
export const fetchBuylistConfig = (storeName) =>
  API.get(`/products/${storeName}/buylist-config`);

/**
 * Get bulk buylist prices for multiple products.
 *
 * Each answer is the store's offer only for the condition that item asked:
 * the engine derives the other conditions' keys from that one base. Read
 * condition prices through utils/buylistConditionQuotes, which asks every
 * condition at itself. A single condition-blind price fetch used to live here
 * and priced LP cards off the NM answer (TKT-2026-106839).
 * @param {string} storeName
 * @param {Array<{ productId: number, printing?: string, condition?: string, language?: string }>} items
 */
export const fetchBulkBuylistPrices = (storeName, items) =>
  API.post(`/products/${storeName}/buylist-price/bulk`, { items });

/**
 * Get available printings for a product
 * @param {string} storeName
 * @param {number} productId
 */
export const fetchBuylistPrintings = (storeName, productId) =>
  API.get(`/buylist/${storeName}/printings/${productId}`);

// ─── LOYALTY API (TKT-2026-106724) ─────────────────────────────────────────────

/**
 * Whether this kiosk shows the points balance screen.
 * Returns: { enabled: false } or { enabled: true, programName, pointName, pointNamePlural }
 * @param {string} storeName
 */
export const fetchLoyaltyProgram = (storeName) =>
  API.get(`/loyalty/${storeName}/program`);

/**
 * Look a points balance up by the email or phone a shopper typed.
 * Every answer, refusals included, is `{ status, ... }`:
 *   found (balance, pointName, pointNamePlural) | multiple | not_found | disabled
 *   | invalid (400) | not_paired (401) | rate_limited (429, retryAfter) | unavailable (503)
 * so this resolves for any status and only rejects when nothing answered.
 * A lookup needs this kiosk's device token (pairKiosk); without one it is not_paired.
 * @param {string} storeName
 * @param {string} identifier
 * @param {string} deviceToken
 */
export const lookupLoyaltyBalance = (storeName, identifier, deviceToken) =>
  API.post(`/loyalty/${storeName}/balance-lookup`, { identifier }, {
    validateStatus: () => true,
    headers: deviceToken ? { 'X-Kiosk-Device-Token': deviceToken } : {},
  });

/**
 * Pair this kiosk for points lookups with the one-time code staff made in
 * SortSwift (Integrations, Kiosk Settings, Pair a kiosk). KioskLoyaltyFinish-1017.
 * Answers `{ status }`: paired (deviceToken) | invalid_code (400)
 *   | rate_limited (429, retryAfter) | unavailable (503)
 * @param {string} storeName
 * @param {string} code
 */
export const pairKiosk = (storeName, code) =>
  API.post(`/loyalty/${storeName}/pair`, { code }, { validateStatus: () => true });

